<template>
  <div>
    <v-col class="justify-space-between wrap d-inline-flex align-center" v-if="idProtectEligible" cols="12">
      <v-switch
        v-if="selected_domain.idprotect === 1"
        v-model="id_protect"
        @change="switchModalState"
      >
        <template v-slot:label>
          <div>
            ID Protect
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon class="mb-1" small @click.stop v-on="on">mdi-information-outline</v-icon>
              </template>
              Hide your information in WHOIS
            </v-tooltip>
            <v-tooltip top v-if="selected_domain.cancellation_id">
              <template v-slot:activator="{ on, attrs }">
                <router-link
                  :to="`/cancellations/${selected_domain.cancellation_id}`"
                  aria-label="View Request"
                  class="pl-1"
                >
                  <v-icon color="orange" small v-on="on" v-bind="attrs">mdi-alert-outline</v-icon>
                </router-link>
              </template>
              <div>Your service will be active until the due date.</div>
              <div>Click to view/update your request.</div>
            </v-tooltip>
          </div>
        </template>
      </v-switch>
      <gg-btn
        v-if="selected_domain.idprotect === 0"
        dark color="green"
        tile elevation="0"

        :loading="loading"
        @click="switchRequestModalState"
      >
        Request ID Protect
      </gg-btn>
    </v-col>

    <v-col v-else cols="12">
      <div class="caption">
        This domain is not eligible for ID Protect.
      </div>
      <div v-if="special_domains" class="caption">
        This domain has registrar enabled WHOIS masking.
      </div>
    </v-col>

    <v-col cols="12" v-if="idProtectWarning">
      <p class="caption" style="color: red">Your information is not protected.</p>
    </v-col>

    <template v-if="idProtectEligible">
      <gg-modal :is-active="request_modal" @close="switchRequestModalState">
        <template v-slot:toolbar>
          Request ID Protect
        </template>
        <template v-slot:body>
          <p>
            Enabling ID Protect will hide your registrant contact information from the public WHOIS database.
            Requesting ID Protect will generate a support ticket for processing by our team.
          </p>
          <v-checkbox color="green" v-model="idProtectAgreement">
            <template v-slot:label>
              <span class="black--text">I confirm the <b>$9.95/year</b> cost for ID Protect</span>
            </template>
          </v-checkbox>
          <p>Would you like to enable ID Protect?</p>
        </template>
        <template v-slot:actions>
          <gg-btn secondary @click="switchRequestModalState">
            Cancel
          </gg-btn>
          <gg-btn primary :disabled="!idProtectAgreement" :loading="loading" @click="requestProtect">
            Request ID Protect
          </gg-btn>
        </template>
      </gg-modal>
    </template>

    <template>
      <gg-modal :is-active="switch_modal" @close="switchModalState('cancel')">
        <template v-slot:toolbar>
          ID Protect
        </template>
        <template v-slot:body>
          <p v-if="id_protect === false">
            Disabling ID Protect will cancel billing for the ID Protect Service Addon. You will not be able to turn ID
            Protect back on once your cancellation is processed.
          </p>
          <p v-if="id_protect === false">Would you like to proceed with cancelling ID Protect?</p>
        </template>
        <template v-slot:actions>
          <gg-btn secondary :loading="loading" @click="switchModalState('cancel')">
            Cancel
          </gg-btn>
          <gg-btn primary @click="cancelIdProtect">
            Turn Off ID Protect
          </gg-btn>
        </template>
      </gg-modal>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { cancellationTypes } from '@/consts/cancellations'

export default {
  name: 'IdProtect',
  props: {
    domainid: {
      type: String,
      required: true
    }
  },
  data: () => ({
    id_protect: false,
    request_modal: false,
    switch_modal: false,
    loading: false,
    idProtectAgreement: false,
  }),
  computed: {
    ...mapGetters(['domains', 'domain', 'domainPopulated', 'services']),
    idProtectEligible () {
      return !!this.domain.idprotect_eligible
    },
    idProtectWarning () {
      return this.idProtectEligible && !this.domain.idprotect_enabled
    },
    selected_domain () {
      return this.domains.find(item => this.domainid === item.id)
    },
    special_domains () {
      const str = this.selected_domain?.domain.indexOf('.ca') || this.selected_domain?.domain.indexOf('.eu') || this.selected_domain?.domain.indexOf('.uk')

      return str !== -1
    }
  },
  watch: {
    domainPopulated () {
      this.setInitialState()
    }
  },
  created () {
    this.setInitialState()
  },
  methods: {
    ...mapActions(['domainIdProtectRequest', 'domainIdProtectToggle', 'updateDomainStore', 'reportSuccess', 'reportError']),
    setInitialState () {
      if (this.domainPopulated) {
        this.id_protect = Boolean(Number(this.domain.idprotect_enabled))
      }
    },
    switchRequestModalState () {
      this.request_modal = !this.request_modal
    },
    switchModalState (action) {
      if (this.isGoingToCancellation()) {
        this.goToCancellation()
        return
      }
      this.switch_modal = !this.switch_modal
      if (action === 'cancel') {
        this.id_protect = !this.id_protect
      }
    },
    async requestProtect () {
      this.loading = true
      try {
        const data = {
          domain_id: this.domainid,
        }
        const response = await this.domainIdProtectRequest(data)
        if (response.result === 'success') {
          this.reportSuccess(response.resultmsg || 'ID Protect request created.')
          this.$router.push(`/support/ticket/${response.reference}`)
        } else {
          this.reportError(response.resultmsg)
        }
        this.loading = false
        this.request_modal = false
      } catch (e) {
        this.loading = false
        this.reportError(e)
      }
    },
    async updateDomain (domainData) {
      try {
        await this.updateDomainStore({
          ...domainData,
          domain_id: this.domainid,
        }, false)
        this.setInitialState()
      } catch (e) {
        this.reportError(e)
      }
    },
    cancelIdProtect () {
      this.$router.push({
        name: 'cancel',
        params: {
          service_id: this.domains.find(domain => parseInt(domain.id) === parseInt(this.domainid)).idprotect_serviceid,
        },
        query: {
          type: cancellationTypes.ID_PROTECT
        }
      })
    },
    isGoingToCancellation () {
      return this.selected_domain.cancellation_id &&
        [cancellationTypes.ID_PROTECT, cancellationTypes.DOMAIN].includes(this.selected_domain.cancellation_type)
    },
    goToCancellation () {
      this.$router.push(`/cancellations/${this.selected_domain.cancellation_id}`)
    },
  }
}
</script>
